import {
  Box,
  Button,
  Drawer,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";

export type ItemMovementRowFilters = {
  name: string;
  sku: string;
  itemType: string;
  transactionReference: string;
};

interface IProps {
  open: boolean;
  handleClose: () => void;
  filters: ItemMovementRowFilters;
  onApply: (next: ItemMovementRowFilters) => void;
  itemTypeOptions: string[];
}

const FilterDrawer = (props: IProps) => {
  const { open, handleClose, filters, onApply, itemTypeOptions } = props;
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const { t: purchasingT } = useTranslation("purchasing");

  const [temp, setTemp] = useState<ItemMovementRowFilters>(filters);

  useEffect(() => {
    if (open) {
      setTemp({
        name: filters.name,
        sku: filters.sku,
        itemType: filters.itemType || "all",
        transactionReference: filters.transactionReference,
      });
    }
  }, [open, filters]);

  const handleApply = () => {
    onApply({
      name: temp.name.trim(),
      sku: temp.sku.trim(),
      itemType: temp.itemType || "all",
      transactionReference: temp.transactionReference.trim(),
    });
    handleClose();
  };

  const handleCancel = () => {
    setTemp({
      name: filters.name,
      sku: filters.sku,
      itemType: filters.itemType || "all",
      transactionReference: filters.transactionReference,
    });
    handleClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleCancel}>
      <Stack
        maxWidth={{ xs: "100%", sm: "376px" }}
        minWidth={{ xs: "100%", sm: "376px" }}
        borderRight={"1px solid #D0D5DD"}
        height={"100%"}
        maxHeight={"100vh"}
        top={"0"}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          p={"20px 27px"}
          bgcolor={"#fff"}
          boxShadow="0px 0px 40px 0px rgba(11, 24, 42, 0.07)"
        >
          <Button
            color="tertiary"
            variant="text"
            size="small"
            endIcon={<img src={FilterIcon} height={"16px"} alt="" />}
          >
            {t("filter")}
          </Button>
          <Button
            variant="outlined"
            color="tertiary"
            size="small"
            onClick={handleCancel}
          >
            {generalT("hide")}
          </Button>
        </Box>
        <Stack spacing={"20px"} p={"40px 27px"}>
          <TextField
            label={t("filterModalItemName")}
            variant="outlined"
            size="small"
            fullWidth
            value={temp.name}
            onChange={(e) => setTemp((prev) => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            label={t("filterModalSku")}
            variant="outlined"
            size="small"
            fullWidth
            value={temp.sku}
            onChange={(e) => setTemp((prev) => ({ ...prev, sku: e.target.value }))}
          />
          <FormControl fullWidth size="small">
            <InputLabel id="item-mv-filter-type">{t("filterModalItemType")}</InputLabel>
            <Select
              labelId="item-mv-filter-type"
              value={temp.itemType || "all"}
              onChange={(e) =>
                setTemp((prev) => ({ ...prev, itemType: String(e.target.value) }))
              }
              input={<OutlinedInput label={t("filterModalItemType")} />}
            >
              <MenuItem value="all">
                <ListItemText primary={generalT("all")} />
              </MenuItem>
              <MenuItem value="direct">
                <ListItemText primary={purchasingT("direct")} />
              </MenuItem>
              <MenuItem value="indirect">
                <ListItemText primary={purchasingT("nonDc")} />
              </MenuItem>
              {itemTypeOptions
                .filter((type) => type !== "direct" && type !== "indirect")
                .map((type) => (
                  <MenuItem key={type} value={type}>
                    <ListItemText primary={type} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label={t("filterModalTransactionReference")}
            variant="outlined"
            size="small"
            fullWidth
            value={temp.transactionReference}
            onChange={(e) =>
              setTemp((prev) => ({
                ...prev,
                transactionReference: e.target.value,
              }))
            }
          />
          <Box width={"100%"} height={"1px"} bgcolor={"#CBD5E1"} />
        </Stack>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          p={"20px 27px"}
          bgcolor={"#fff"}
          boxShadow="0px 0px 40px 0px rgba(11, 24, 42, 0.07)"
          alignItems={"flex-end"}
          mt="auto"
        >
          <Button variant="contained" size="small" onClick={handleApply}>
            {generalT("apply")}
          </Button>
          <Button
            variant="outlined"
            color="tertiary"
            size="small"
            onClick={handleCancel}
          >
            {generalT("cancel")}
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default FilterDrawer;
